/**
 * Phase-4 — Teacher-scoped Report Builders.
 *
 * 5 reports × 3 formats (PDF / XLSX / CSV) — all generated server-side by
 * /api/school/portal/teacher/reports/{kind}.{fmt}.
 *
 *   • attendance          → optional class + date-range filter
 *   • grades              → optional subject filter
 *   • assignments         → optional subject filter
 *   • exams               → no filter (teacher's own exams)
 *   • student-progress    → required student_id (scoped to teacher's classes)
 *
 * Downloads run through an authenticated fetch so the bearer token reaches
 * the backend; the resulting Blob is offered to the browser via a hidden
 * anchor tag — no third-party libs needed.
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  FileText, Download, Loader2, CalendarRange, BookOpen, ClipboardList,
  GraduationCap, UserSearch,
} from 'lucide-react';
import { portalApi } from './teacherApi';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FORMATS = [
  { id: 'pdf',  label: 'PDF',  ext: 'pdf' },
  { id: 'xlsx', label: 'Excel', ext: 'xlsx' },
  { id: 'csv',  label: 'CSV',  ext: 'csv' },
];

/** Fetch a binary report from the backend and trigger a download. */
const downloadReport = async (kind, fmt, params = {}) => {
  const qs = new URLSearchParams(params).toString();
  const url = `${API_URL}/api/school/portal/teacher/reports/${kind}.${fmt}${qs ? `?${qs}` : ''}`;
  const pt = (() => { try { return sessionStorage.getItem('school_preview_token'); } catch { return null; } })();
  const token = pt || getToken();
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    let detail = `Request failed (${res.status})`;
    try { detail = (await res.json()).detail || detail; } catch { /* binary or empty */ }
    throw new Error(detail);
  }
  const cd = res.headers.get('Content-Disposition') || '';
  const m = cd.match(/filename="([^"]+)"/);
  const filename = m ? m[1] : `${kind}.${fmt}`;
  const blob = await res.blob();
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
};

/** A small "PDF · Excel · CSV" button group with loading state per format. */
const FormatButtons = ({ kind, params, disabled, testidPrefix }) => {
  const [busy, setBusy] = useState(null);
  const run = async (fmt) => {
    if (disabled) return;
    setBusy(fmt);
    try {
      await downloadReport(kind, fmt, params || {});
      toast.success(`${kind.replace('-', ' ')} · ${fmt.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(e.message || 'Download failed');
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="flex flex-wrap gap-2">
      {FORMATS.map(f => (
        <Button
          key={f.id}
          size="sm"
          variant={f.id === 'pdf' ? 'default' : 'outline'}
          disabled={disabled || !!busy}
          onClick={() => run(f.id)}
          data-testid={`${testidPrefix}-download-${f.id}`}
        >
          {busy === f.id
            ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
            : <Download className="h-3.5 w-3.5 mr-1" />}
          {f.label}
        </Button>
      ))}
    </div>
  );
};

const SectionCard = ({ icon: Icon, title, blurb, children, testid }) => (
  <Card data-testid={testid}>
    <CardContent className="pt-5">
      <div className="flex items-start gap-3 mb-3">
        <div className="rounded-md bg-indigo-50 p-2 text-indigo-600">
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-sm">{title}</h3>
          <p className="text-xs text-slate-500 mt-0.5">{blurb}</p>
        </div>
      </div>
      <div className="space-y-3">{children}</div>
    </CardContent>
  </Card>
);

export const TeacherReports = () => {
  // -------------------- reference data --------------------
  const { data: subjects = [] } = useQuery({
    queryKey: ['teacher-subjects'],
    queryFn: () => portalApi('/teacher/subjects'),
  });
  const { data: students = [] } = useQuery({
    queryKey: ['teacher-students'],
    queryFn: () => portalApi('/teacher/students'),
  });
  const { data: classes = [] } = useQuery({
    queryKey: ['teacher-classes'],
    queryFn: () => portalApi('/teacher/classes'),
  });

  // -------------------- per-report filter state --------------------
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString().slice(0, 10);
  const [attFilter, setAttFilter] = useState({ class_id: 'all', date_from: monthAgo, date_to: today });
  const [gradeFilter, setGradeFilter] = useState({ subject_id: 'all' });
  const [asgnFilter, setAsgnFilter] = useState({ subject_id: 'all' });
  const [studentId, setStudentId] = useState('');

  const buildParams = (obj) => {
    const out = {};
    Object.entries(obj).forEach(([k, v]) => {
      if (v && v !== 'all') out[k] = v;
    });
    return out;
  };

  return (
    <div className="space-y-4" data-testid="teacher-reports-page">
      <div>
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <FileText className="h-5 w-5 text-indigo-500" /> Reports
        </h2>
        <p className="text-xs text-slate-500 mt-0.5">
          Native, teacher-scoped report builders. Filter, then download as PDF · Excel · CSV.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* ---------------- ATTENDANCE ---------------- */}
        <SectionCard
          icon={CalendarRange}
          title="Attendance reports"
          blurb="Daily attendance for students in classes you teach."
          testid="teacher-report-attendance"
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Class</Label>
              <Select
                value={attFilter.class_id}
                onValueChange={(v) => setAttFilter(f => ({ ...f, class_id: v }))}
              >
                <SelectTrigger className="h-8" data-testid="attendance-class-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All my classes</SelectItem>
                  {classes.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">From</Label>
              <Input
                type="date"
                className="h-8"
                value={attFilter.date_from}
                onChange={(e) => setAttFilter(f => ({ ...f, date_from: e.target.value }))}
                data-testid="attendance-date-from"
              />
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input
                type="date"
                className="h-8"
                value={attFilter.date_to}
                onChange={(e) => setAttFilter(f => ({ ...f, date_to: e.target.value }))}
                data-testid="attendance-date-to"
              />
            </div>
          </div>
          <FormatButtons
            kind="attendance"
            params={buildParams(attFilter)}
            testidPrefix="attendance"
          />
        </SectionCard>

        {/* ---------------- GRADES ---------------- */}
        <SectionCard
          icon={GraduationCap}
          title="Grade reports"
          blurb="Recorded marks across the subjects you teach."
          testid="teacher-report-grades"
        >
          <div>
            <Label className="text-xs">Subject</Label>
            <Select
              value={gradeFilter.subject_id}
              onValueChange={(v) => setGradeFilter({ subject_id: v })}
            >
              <SelectTrigger className="h-8" data-testid="grades-subject-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All my subjects</SelectItem>
                {subjects.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <FormatButtons kind="grades" params={buildParams(gradeFilter)} testidPrefix="grades" />
        </SectionCard>

        {/* ---------------- ASSIGNMENTS ---------------- */}
        <SectionCard
          icon={ClipboardList}
          title="Assignment reports"
          blurb="Submission counts, graded/pending, late submissions."
          testid="teacher-report-assignments"
        >
          <div>
            <Label className="text-xs">Subject</Label>
            <Select
              value={asgnFilter.subject_id}
              onValueChange={(v) => setAsgnFilter({ subject_id: v })}
            >
              <SelectTrigger className="h-8" data-testid="assignments-subject-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All my subjects</SelectItem>
                {subjects.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <FormatButtons kind="assignments" params={buildParams(asgnFilter)} testidPrefix="assignments" />
        </SectionCard>

        {/* ---------------- EXAMS ---------------- */}
        <SectionCard
          icon={BookOpen}
          title="Exam reports"
          blurb="Submissions, average score and pass-rate for exams you created."
          testid="teacher-report-exams"
        >
          <p className="text-xs text-slate-500">
            Includes all exams where you are the creator.
          </p>
          <FormatButtons kind="exams" params={{}} testidPrefix="exams" />
        </SectionCard>

        {/* ---------------- STUDENT PROGRESS ---------------- */}
        <SectionCard
          icon={UserSearch}
          title="Student progress reports"
          blurb="30-day attendance + marks + risk flag for a single student."
          testid="teacher-report-progress"
        >
          <div>
            <Label className="text-xs">Student</Label>
            <Select value={studentId} onValueChange={setStudentId}>
              <SelectTrigger className="h-8" data-testid="progress-student-select">
                <SelectValue placeholder="Pick a student…" />
              </SelectTrigger>
              <SelectContent>
                {students.length === 0 && (
                  <SelectItem value="__none__" disabled>No students in your classes</SelectItem>
                )}
                {students.map(s => (
                  <SelectItem key={s.id} value={s.id}>
                    {`${s.first_name || ''} ${s.last_name || ''}`.trim() || s.admission_number || s.id}
                    {s.admission_number ? ` · ${s.admission_number}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <FormatButtons
            kind="student-progress"
            params={studentId ? { student_id: studentId } : {}}
            disabled={!studentId}
            testidPrefix="progress"
          />
          {!studentId && (
            <p className="text-[11px] text-slate-400">
              Pick a student above to enable download.
            </p>
          )}
        </SectionCard>
      </div>
    </div>
  );
};

export default TeacherReports;
